import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, CreditCard, Loader2, ShieldCheck, Users, AlertCircle } from "lucide-react";
import { TermsAcceptance } from "./TermsAcceptance";
import { SectionTitle, Prose, Field } from "./primitives";

const KIND_COPY = {
  deposit: { tag: "My deposit", pct: "30%", helper: "Secures your spot on the trip. Your remaining balance is due 60 days before departure." },
  complete_deposit: { tag: "Complete my deposit", pct: null, helper: "Finishes the 30% deposit on your share.", highlight: true },
  balance: { tag: "My remaining balance", pct: null, helper: "Settles everything left on your share." },
  full: { tag: "My full share", pct: "100%", helper: "One payment covering your whole share of the trip." },
};

function statusOf(s) {
  if (s.fully_paid) return { text: "Paid in full", cls: "bg-espiritu-olive/15 text-espiritu-olive" };
  if (s.deposit_paid) return { text: "Deposit paid", cls: "bg-espiritu-sand-deep/60 text-espiritu-deep" };
  if (s.paid > 0) return { text: "Partially paid", cls: "bg-amber-100 text-amber-800" };
  return { text: "Not paid yet", cls: "bg-white text-espiritu-deep/60 border border-espiritu-sand-deep" };
}

/** Public /pay/{token} in custom-split mode: each traveler picks their
 *  name and pays ONLY their own share (deposit / balance / other amount). */
export function PayerShareSection({ shares, fmt: fmtWhole, currency = "USD", preselectedId, onPay, submittingKind, tosAccepted, onTosChange, error, fullRequired }) {
  // Share amounts can carry cents ($1,553.50) — show them exactly as PayPal
  // will charge instead of rounding to whole units like the generic flow.
  const fmt = (n) => {
    const v = Math.round((n || 0) * 100) / 100;
    if (Number.isInteger(v)) return fmtWhole(v);
    return new Intl.NumberFormat(currency === "EUR" ? "es-ES" : "en-US", { style: "currency", currency, minimumFractionDigits: 2 }).format(v);
  };
  const [selectedId, setSelectedId] = useState(preselectedId || null);
  const [payerName, setPayerName] = useState("");
  const [payerEmail, setPayerEmail] = useState("");
  const [customAmount, setCustomAmount] = useState("");

  const share = useMemo(() => shares.find((s) => s.share_id === selectedId) || null, [shares, selectedId]);
  useEffect(() => {
    if (!share) return;
    setPayerName(share.name || "");
    setPayerEmail(share.email || "");
    setCustomAmount(share.partial_bounds?.min ? String(share.partial_bounds.min) : "");
  }, [share?.share_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((payerEmail || "").trim());
  const disabled = submittingKind !== null || !tosAccepted || !emailValid || !payerName.trim();
  const meta = share ? { share_id: share.share_id, payer_name: payerName.trim(), payer_email: payerEmail.trim(), share_label: share.name } : {};
  const num = parseFloat(customAmount);
  const bMin = share?.partial_bounds?.min || 0;
  const bMax = share?.partial_bounds?.max || 0;
  const customValid = !isNaN(num) && num >= bMin - 0.01 && num <= bMax + 0.01;

  return (
    <section className="mt-12" data-testid="payer-shares-section">
      <SectionTitle icon={<Users size={14}/>}>Who are you?</SectionTitle>
      <Prose>
        <p>
          This trip is being paid by <strong>{shares.length} travelers</strong>, each covering their own share.
          Select your name to see your amount{fullRequired ? "" : ", your 30% deposit"} and what you&apos;ve already paid.
        </p>
      </Prose>
      <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="share-list">
        {shares.map((s) => {
          const st = statusOf(s);
          const active = s.share_id === selectedId;
          return (
            <button key={s.share_id} type="button" onClick={() => setSelectedId(s.share_id)}
                    data-testid={`share-card-${s.share_id}`}
                    className={`text-left px-4 py-3 bg-white border transition-colors ${active ? "border-espiritu-terra ring-1 ring-espiritu-terra" : "border-espiritu-sand-deep hover:border-espiritu-terra/60"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-kanit font-bold text-espiritu-deep truncate">{s.name}</div>
                <span className={`shrink-0 px-2 py-0.5 text-[10px] uppercase tracking-wider ${st.cls}`}>{st.text}</span>
              </div>
              <div className="mt-1 font-serif tabular text-xl text-espiritu-deep">{fmt(s.amount)}</div>
              {s.paid > 0 && !s.fully_paid && (
                <div className="font-raleway text-[11px] text-espiritu-deep/60">{fmt(s.paid)} paid · {fmt(s.remaining)} left</div>
              )}
            </button>
          );
        })}
      </div>

      {share && (
        <div className="mt-8" data-testid="share-detail">
          <SectionTitle icon={<CreditCard size={14}/>}>{share.name} — your share</SectionTitle>
          <div className="grid grid-cols-3 bg-white border border-espiritu-sand-deep">
            {[["Your share", share.amount, ""], ["Paid", share.paid, "text-espiritu-olive"], ["Remaining", share.remaining, "text-espiritu-terra"]].map(([l, v, c]) => (
              <div key={l} className="px-5 py-4 border-r border-espiritu-sand-deep last:border-r-0">
                <div className="kicker">{l}</div>
                <div className={`font-serif tabular text-2xl mt-1 ${c}`} data-testid={`share-${l.toLowerCase().replace(" ", "-")}`}>{fmt(v)}</div>
              </div>
            ))}
          </div>

          {share.fully_paid ? (
            <div className="mt-5 border-l-4 border-espiritu-olive bg-white px-5 py-4 font-kanit font-bold text-espiritu-deep inline-flex items-center gap-3 w-full" data-testid="share-fully-paid">
              <CheckCircle2 size={20} className="text-espiritu-olive"/> Your share is fully paid — thank you!
            </div>
          ) : (
            <>
              <div className="bg-white border border-espiritu-sand-deep px-5 py-5 mt-5 grid gap-3 md:grid-cols-2">
                <Field label="Your full name">
                  <input value={payerName} onChange={(e) => setPayerName(e.target.value)} data-testid="share-payer-name" className="brand-input"/>
                </Field>
                <Field label="Your email (required — for your receipt)">
                  <input value={payerEmail} onChange={(e) => setPayerEmail(e.target.value)} data-testid="share-payer-email" type="email" className="brand-input" placeholder="you@example.com"/>
                </Field>
              </div>

              <div className={`mt-5 grid gap-5 ${share.options.length + 1 > 1 ? "sm:grid-cols-2" : ""}`}>
                {share.options.map((o) => {
                  const d = KIND_COPY[o.kind] || { tag: o.label, helper: "" };
                  return (
                    <div key={o.kind} data-testid={`share-option-${o.kind}`}
                         className={`p-7 flex flex-col ${d.highlight ? "bg-espiritu-sand-deep/40 border-2 border-espiritu-olive" : "bg-white border border-espiritu-sand-deep"}`}>
                      <div className="flex items-baseline justify-between">
                        <div className="kicker">{d.tag}</div>
                        {d.pct && <div className="font-serif italic text-espiritu-terra text-2xl leading-none">{d.pct}</div>}
                      </div>
                      <div className="font-serif tabular text-5xl mt-3 text-espiritu-deep">{fmt(o.amount)}</div>
                      <div className="font-raleway text-sm text-espiritu-deep/70 mt-3 flex-1 leading-relaxed">{d.helper}</div>
                      <button onClick={() => onPay(o.kind, undefined, meta)} disabled={disabled}
                              data-testid={`share-pay-btn-${o.kind}`}
                              className="mt-6 inline-flex items-center justify-center gap-2 bg-espiritu-deep hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed text-white px-5 py-3.5 rounded-full text-sm font-medium transition-colors">
                        {submittingKind === o.kind
                          ? <><Loader2 size={14} className="animate-spin"/> Redirecting to PayPal…</>
                          : <><CreditCard size={14}/> Pay {fmt(o.amount)}</>}
                      </button>
                    </div>
                  );
                })}
                <div className="bg-white border border-espiritu-sand-deep p-7 flex flex-col" data-testid="share-option-partial">
                  <div className="flex items-baseline justify-between">
                    <div className="kicker">Other amount</div>
                    <div className="font-serif italic text-espiritu-terra text-lg leading-none">You choose</div>
                  </div>
                  <div className="mt-3 relative">
                    <input value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} inputMode="decimal"
                           data-testid="share-partial-input" className="brand-input font-serif text-4xl tabular pl-3"/>
                  </div>
                  <div className="mt-1.5 font-raleway text-[11px] text-espiritu-deep/60">
                    Between <span className="tabular">{fmt(bMin)}</span> and <span className="tabular">{fmt(bMax)}</span>.
                  </div>
                  <div className="font-raleway text-xs text-espiritu-deep/70 mt-4 flex-1 leading-relaxed">
                    {share.deposit_paid ? "Pay any instalment towards your balance." : "At least your deposit, so your spot is secured."} Come back to this link whenever you want to pay more.
                  </div>
                  <button onClick={() => onPay("partial", num, meta)} disabled={disabled || !customValid}
                          data-testid="share-pay-btn-partial"
                          className="mt-5 inline-flex items-center justify-center gap-2 bg-espiritu-deep hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-3.5 rounded-full text-sm font-medium transition-colors">
                    {submittingKind === "partial"
                      ? <><Loader2 size={14} className="animate-spin"/> Redirecting to PayPal…</>
                      : <><CreditCard size={14}/> Pay {customValid ? fmt(num) : "—"}</>}
                  </button>
                </div>
              </div>

              {!payerName.trim() && <div className="mt-3 font-raleway text-xs text-espiritu-magenta">Enter your full name above so we can log your share.</div>}
              {!emailValid && <div className="mt-2 font-raleway text-xs text-espiritu-magenta">Enter a valid email above — we&apos;ll send your receipt there.</div>}
              <TermsAcceptance accepted={tosAccepted} onChange={onTosChange}/>
              {!tosAccepted && (
                <div className="mt-2 font-raleway text-xs text-espiritu-deep/60 flex items-center gap-1">
                  <AlertCircle size={12} className="text-espiritu-magenta"/> Please accept the Terms &amp; Conditions to continue.
                </div>
              )}
            </>
          )}
          <div className="mt-5 font-raleway text-xs text-espiritu-deep/60 inline-flex items-center gap-2">
            <ShieldCheck size={14}/> Secure checkout via PayPal — credit/debit cards accepted, no PayPal account required.
          </div>
          {error && (
            <div className="mt-5 border-l-4 border-espiritu-magenta bg-white px-4 py-3 text-sm font-raleway text-espiritu-deep" data-testid="payment-inline-error">{error}</div>
          )}
        </div>
      )}
    </section>
  );
}
